import React, { useEffect, useMemo, useState } from 'react';

import { Spinner } from '@/components/ui/spinner';
import {
  SitrepCardSurface,
  type GetSitrepsResponse,
  type SitrepViewDataProps,
} from 'components/Reports/reports.types';
import { ReportsEmptyState } from 'components/Reports/ReportsEmptyState';
import useOpenReportSelection from 'components/Reports/hooks/useOpenReportSelection';
import ReportsSitrepCard from 'components/Reports/ReportsSitrepCard';
import { useAorText } from 'utils/aorTerminology';

type DraftReportViewProps = {
  draftSitreps: SitrepViewDataProps;
  pendingReviews: SitrepViewDataProps;
  scopeName: string;
  derivedAorId?: string | null;
};

type DraftListCard = {
  sitrep: GetSitrepsResponse;
  surface: SitrepCardSurface;
};

/** Header first, so the tour can spotlight the card before the document renders. */
const DeferredDraftCard = ({ sitrep, surface }: DraftListCard) => {
  const { isOpenReportMatch } = useOpenReportSelection(
    sitrep.id,
    sitrep.item_id
  );
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const frame = window.requestAnimationFrame(() => setReady(true));
    return () => window.cancelAnimationFrame(frame);
  }, []);
  const tourTarget =
    (surface === SitrepCardSurface.Draft ||
      surface === SitrepCardSurface.Review) &&
    isOpenReportMatch;
  return (
    <div
      className="reports-card"
      data-tour={tourTarget ? 'reports-draft-card' : undefined}
    >
      {ready ? (
        <ReportsSitrepCard sitrep={sitrep} surface={surface} />
      ) : (
        <p className="tw:px-3 tw:py-2.5 tw:text-sm tw:font-medium">
          {sitrep.name}
        </p>
      )}
    </div>
  );
};

const DraftReportView: React.FC<DraftReportViewProps> = ({
  draftSitreps,
  pendingReviews,
  scopeName,
  derivedAorId,
}) => {
  const aorText = useAorText();
  const cards = useMemo(() => {
    const reviewById = new Map(
      pendingReviews.data.map((sitrep) => [sitrep.id, sitrep])
    );
    const draftIds = new Set(draftSitreps.data.map((sitrep) => sitrep.id));
    const merged: DraftListCard[] = draftSitreps.data.map((sitrep) => {
      const review = reviewById.get(sitrep.id);
      return review
        ? { sitrep: review, surface: SitrepCardSurface.Review }
        : { sitrep, surface: SitrepCardSurface.Draft };
    });
    for (const sitrep of pendingReviews.data) {
      if (!draftIds.has(sitrep.id)) {
        merged.push({ sitrep, surface: SitrepCardSurface.Review });
      }
    }
    return merged;
  }, [draftSitreps.data, pendingReviews.data]);

  const isLoading = draftSitreps.isLoading || pendingReviews.isLoading;
  const isError = draftSitreps.isError || pendingReviews.isError;

  return (
    <div className="reports-tabs">
      {!derivedAorId ? (
        <ReportsEmptyState
          icon="error"
          title={aorText('No Area of Responsibility')}
          description={aorText(
            'Unable to determine Area of Responsibility for this incident. Please ensure the incident is associated with an AOR in order to create and view draft SITREPs.'
          )}
        />
      ) : cards.length > 0 ? (
        cards.map(({ sitrep, surface }) => (
          <DeferredDraftCard
            key={sitrep.id}
            sitrep={sitrep}
            surface={surface}
          />
        ))
      ) : isLoading ? (
        <Spinner className="tw:size-6" />
      ) : isError ? (
        <ReportsEmptyState
          icon="error"
          title="Error loading reports"
          description={aorText(
            'Try refreshing or selecting a different AOR or incident.'
          )}
        />
      ) : (
        <ReportsEmptyState
          title={`No reports for ${scopeName} yet`}
          description="Create a draft report to get started."
        />
      )}
    </div>
  );
};

export default DraftReportView;
